import { useQueryClient as useTanStackQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';

import { useAuth } from '@src/features/auth/contexts/auth-context';
import { reservationBreakfastStatusQueryKey } from '@src/features/breakfast-info/use-reservation-breakfast-status.query';
import { useLocale } from '@src/hooks/use-locale';
import { tripService } from '@src/services';
import { Trip } from '@src/types/api/models';
import { useDash0IdentityAttributes } from '@src/utils/observability/use-dash0-identity';

import { TripContext } from './trip.context';
import { TripPollingCondition, TripPollingOptions, TripProviderProps } from './types';

const getKey = (trip: Trip | null, locale: string) => ['trip', trip?.id, locale];
export const TripProvider = ({ value, children }: TripProviderProps) => {
  const { trip } = value;
  const { isReady } = useAuth();
  const locale = useLocale();
  const queryClient = useQueryClient();
  const tanStackQueryClient = useTanStackQueryClient();

  const queryKey = getKey(trip, locale);
  const queryFn = () => tripService.getTripById(trip?.id as string, locale);
  const queryOptions = {
    initialData: () => trip,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: !!trip,
  };
  const tripQuery = useQuery(queryKey, queryFn, queryOptions);

  /**
   * Trips can be polled until a certain condition is met.
   * The condition should return false if no further polling is needed or a timeout in ms to poll again.
   * The Provider returns a function pollTrip(condition) that returns a Promise and resolves when the condition is met.
   */
  const [tripPollingCondition, setTripPollingCondition] = useState<TripPollingCondition | undefined>(undefined);
  const [pollingResolver, setPollingResolver] = useState<(trip: Trip) => void>();
  useQuery([...queryKey, 'polling'], queryFn, {
    ...queryOptions,
    enabled: queryOptions.enabled && !!tripPollingCondition,
    refetchInterval: tripPollingCondition,
    refetchIntervalInBackground: true,
    onSuccess: (data) => {
      if (data && tripPollingCondition?.(data) === false) {
        pollingResolver?.(data);
        queryClient.setQueryData(queryKey, data);
        setTripPollingCondition(undefined);
      }
    },
  });

  // Associates the reservation with all browser telemetry (errors, traces,
  // web events) while a trip-scoped page is mounted. Safe no-op when the
  // Dash0 SDK is uninitialized (local dev).
  useDash0IdentityAttributes({
    reservationId: tripQuery.data?.id,
    propertyId: tripQuery.data?.property.id,
  });

  const linkedTripsQuery = useQuery(
    ['linkedTrips', trip?.id],
    () => {
      return tripService.getLinkedTrips(trip?.id as string, locale);
    },

    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      enabled: !!trip && isReady,
    },
  );

  if (!tripQuery.data) {
    return <TripContext.Provider value={null}>{children}</TripContext.Provider>;
  }

  const breakfastStatusQueryKey = reservationBreakfastStatusQueryKey(tripQuery.data.id);

  return (
    <TripContext.Provider
      value={{
        trip: tripQuery.data,
        linkedTrips: linkedTripsQuery?.data,
        // `isFetched` (not `isLoading`) so the flag also covers the idle phase
        // before auth is ready, yet stays settled during background refetches —
        // consumers gating UI on it must not flicker when the query revalidates.
        isLinkedTripsLoading: !linkedTripsQuery.isFetched,
        updateTrip: async () => {
          await queryClient.invalidateQueries(queryKey);
          await tripQuery.refetch();
          // Read once the refreshed trip is back, so the status sees the purchase the trip does.
          void tanStackQueryClient.invalidateQueries({ queryKey: breakfastStatusQueryKey });

          setTimeout(() => queryClient.invalidateQueries(['linkedTrips', tripQuery.data?.id]), 5000);
        },
        pollTrip: async (until: TripPollingCondition, options?: TripPollingOptions): Promise<Trip> => {
          setTimeout(() => {
            setTripPollingCondition(() => until);
          }, options?.initialDelay ?? 0);

          return new Promise((resolve) => {
            setPollingResolver(() => resolve);
          });
        },
      }}
    >
      {children}
    </TripContext.Provider>
  );
};
